/**
 * @file contains functions used to create urls to reroute via <Link /> or navigate
 * Can be same with the path routes, but we keep urls to be future-proof
 */

import {
  createSearchParams,
  Location,
  URLSearchParamsInit,
} from 'react-router-dom';

import {
  SEARCH_PARAM__COMPOSE,
  SEARCH_PARAM__COMPOSE_MODE,
  SEARCH_PARAM__COMPOSE_TYPE,
} from '../../../constants/routing/searchParams';
import { Hotel, Template } from '../../../generated/graphql';

import { ComposeModeParam, ComposeTypeParam } from '../../../models/pageParams';
import { SearchParamsKey } from '../../../models/searchParams';
import { reportError } from '../../../services/reporting';

import {
  parseUrlString,
  removeParamsKeysFromUrl,
  replaceOrAddParamsToUrl,
} from '../../helpers/router';
import { getSearchParamCompose } from '../../helpers/searchParams';
import { getSettingsTemplatesUrl } from '../settings';

/**
 * Gets url that adds `?compose=open` search param
 *
 * @param location    Location object from the history
 * @param composeType Type of object for compose (post or task)
 * @returns           A string in a url format
 */
export const getOpenComposeUrl = (
  location: Location,
  composeType: ComposeTypeParam | null,
): string => {
  return replaceOrAddParamsToUrl(location, {
    [SEARCH_PARAM__COMPOSE]: 'open',
    ...(composeType !== null
      ? { [SEARCH_PARAM__COMPOSE_TYPE]: composeType }
      : {}),
  });
};

/**
 * Generate URL for template edit
 *
 * @param hotelId     The ID of the currently open hotel
 * @param isTablet    Whether the use is on a tablet
 * @param templateId  The ID of the template to generate the url for
 * @param composeType Type of object for compose (post or task)
 * @returns           The generated URL
 */
export const getOpenComposeUrlTemplate = (
  hotelId: Hotel['id'] | null,
  isTablet: boolean,
  templateId: Template['id'],
  composeType: ComposeTypeParam | null,
) => {
  if (hotelId === null) {
    reportError('Hotel ID not found: ' + window.location.href);
    return '/';
  }

  const url = getSettingsTemplatesUrl(hotelId, isTablet, templateId);
  const [pathname, search] = parseUrlString(url);

  const paramsConfig: URLSearchParamsInit = {
    [SEARCH_PARAM__COMPOSE]: 'open',
    ...(composeType !== null
      ? { [SEARCH_PARAM__COMPOSE_TYPE]: composeType }
      : {}),
  };

  const params = createSearchParams(paramsConfig);
  const paramsFinal = `${search ? `${search}&` : '?'}${params.toString()}`;

  return `${pathname}${paramsFinal}`;
};

/**
 * Gets url that removes `?compose=open` search param
 *
 * @param location       Location object from the history
 * @param isDraftChanged Whether the draft has been changed
 * @returns              A string in a url format
 */
export const getCloseComposeUrl = (
  location: Location,
  isDraftChanged = false,
): string => {
  const additionalParams: SearchParamsKey[] = isDraftChanged
    ? []
    : [SEARCH_PARAM__COMPOSE_TYPE];

  return removeParamsKeysFromUrl(
    location,
    SEARCH_PARAM__COMPOSE,
    SEARCH_PARAM__COMPOSE_MODE,
    ...additionalParams,
  );
};

/**
 * Gets url that adds `?composeMode={section}` search param
 *
 * @param location    Location object from the history
 * @param sectionName Name of the section
 * @returns           A string in a url format
 */
export const getOpenComposeSectionUrl = (
  location: Location,
  sectionName: ComposeModeParam,
): string => {
  return replaceOrAddParamsToUrl(location, { composeMode: sectionName });
};

/**
 * Gets url that adds `?composeType={type}` search param
 *
 * @param location    Location object from the history
 * @param composeType Type of object for compose (post or task)
 * @returns           A string in a url format
 */
export const getComposeTypeUrl = (
  location: Location,
  composeType: ComposeTypeParam,
): string => {
  return replaceOrAddParamsToUrl(location, {
    [SEARCH_PARAM__COMPOSE_TYPE]: composeType,
  });
};

/**
 * Gets url that removes `?composeMode={section}` search param
 *
 * @param location Location object from the history
 * @returns        A string in a url format
 */
export const getCloseComposeSectionUrl = (location: Location): string => {
  return removeParamsKeysFromUrl(location, SEARCH_PARAM__COMPOSE_MODE);
};

/**
 * Gets url for minimizing or maximizing the compose form based on current location
 *
 * @param location Location object from the history
 * @returns        A string in a url format
 */
export const getComposeMinimizeToggleUrl = (location: Location): string => {
  const isOpen = getSearchParamCompose(location) === 'open';

  const newUrl = isOpen
    ? removeParamsKeysFromUrl(location, SEARCH_PARAM__COMPOSE)
    : replaceOrAddParamsToUrl(location, {
        [SEARCH_PARAM__COMPOSE]: 'open',
      });

  return newUrl;
};
